import { 
  TulaConfig, 
  Player, 
  Event, 
  PlayerIdentificationRequest, 
  PlayerIdentificationResponse, 
  ApiResponse,
  ItemVariantResponse,
  GetItemVariantRequest,
  GetAllVariantsRequest,
  GetAllVariantsResponse,
  LogEventRequest,
  LogEventResponse
} from './types';
import { SessionManager } from './session-manager';
import { EventManager } from './event-manager';
import { AutoTracker } from './auto-tracker';
import { generateId, debugLog, getDeviceId, getPlatform, getAppVersion } from './utils';

export class TulaSDK {
  private config: TulaConfig;
  private sessionManager: SessionManager;
  private eventManager: EventManager;
  private autoTracker: AutoTracker;
  private currentPlayer: Player | null = null;
  private initialized = false;
  private variantCache: Record<string, ItemVariantResponse> = {};

  constructor(config: TulaConfig) {
    this.config = {
      baseUrl: 'http://localhost:3000', // Development endpoint
      debug: false,
      batchSize: 10,
      flushInterval: 5000,
      ...config
    };

    this.sessionManager = new SessionManager(
      this.config.apiKey,
      this.config.baseUrl!,
      this.config.debug,
      this.config.platform
    );
    this.eventManager = new EventManager(
      this.config.apiKey,
      this.config.baseUrl!,
      this.config.batchSize!,
      this.config.flushInterval!,
      this.config.debug
    );
    this.autoTracker = new AutoTracker(this, this.config.debug);

    debugLog(this.config.debug, 'TulaSDK initialized with config:', this.config);
  }

  async initialize(externalPlayerId?: string, playerMetadata?: Record<string, any>): Promise<void> {
    if (this.initialized) {
      debugLog(this.config.debug, 'SDK already initialized');
      return;
    }

    const externalId = externalPlayerId || this.generateExternalPlayerId();
    
    // Identify player through backend API - this creates player if doesn't exist
    const player = await this.identifyPlayer(externalId, playerMetadata);
    
    this.currentPlayer = player;
    this.eventManager.setCurrentPlayer(player.externalPlayerId);
    await this.sessionManager.startSession(player);

    // Fetch and cache all variants for this player
    await this.fetchAndCacheAllVariants();

    this.initialized = true;

    // Start auto-tracking after initialization
    this.autoTracker.start();

    debugLog(this.config.debug, 'SDK initialized for player:', player.externalPlayerId, 'with internal ID:', player.id);
  }

  // Track when user views the general store page (generates store_view events)
  trackStoreView(properties?: Record<string, any>): void {
    this.ensureInitialized();

    const session = this.sessionManager.getCurrentSession();
    if (!session) {
      debugLog(this.config.debug, 'No active session for store view tracking');
      return;
    }

    this.eventManager.addEvent({
      playerId: this.currentPlayer!.id,
      sessionId: session.id,
      eventType: 'store_view',
      properties: {
        page: 'store',
        ...properties
      }
    });

    debugLog(this.config.debug, 'Store view tracked');
  }


  trackCustomEvent(eventType: string, properties?: Record<string, any>): void {
    this.ensureInitialized();

    const session = this.sessionManager.getCurrentSession();
    if (!session) {
      debugLog(this.config.debug, 'No active session for custom event tracking');
      return;
    }

    this.eventManager.addEvent({
      playerId: this.currentPlayer!.id,
      sessionId: session.id,
      eventType: 'custom',
      properties: {
        custom_event_type: eventType,
        ...properties
      }
    });

    debugLog(this.config.debug, 'Custom event tracked:', eventType);
  }

  async flush(): Promise<void> {
    await this.eventManager.flush();
  }

  async destroy(): Promise<void> {
    this.autoTracker.stop();
    await this.sessionManager.endSession();
    await this.eventManager.destroy();
    this.initialized = false;
    this.currentPlayer = null;
    this.variantCache = {};
    debugLog(this.config.debug, 'SDK destroyed');
  }

  private ensureInitialized(): void {
    if (!this.initialized || !this.currentPlayer) {
      throw new Error('SDK must be initialized before tracking events. Call initialize() first.');
    }
  }

  private async identifyPlayer(externalPlayerId: string, metadata?: Record<string, any>): Promise<Player> {
    const request: PlayerIdentificationRequest = {
      externalPlayerId,
      deviceId: getDeviceId(),
      platform: getPlatform(this.config.platform),
      appVersion: getAppVersion(),
      metadata: metadata || {}
    };

    debugLog(this.config.debug, 'Identifying player:', externalPlayerId);

    try {
      const response = await fetch(`${this.config.baseUrl}/api/players/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-SDK-Version': '0.1.1'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiResponse: ApiResponse<PlayerIdentificationResponse> = await response.json();
      
      if (!apiResponse.success || !apiResponse.data) {
        throw new Error(apiResponse.error || 'Player identification failed');
      }

      const playerData = apiResponse.data;
      debugLog(this.config.debug, 'Player identified:', playerData.id, 'isNew:', playerData.isNewPlayer);

      return {
        id: playerData.id,
        externalPlayerId: playerData.externalPlayerId,
        metadata: metadata || {}
      };
    } catch (error) {
      debugLog(this.config.debug, 'Player identification failed:', error);
      throw new Error(`Failed to identify player: ${error}`);
    }
  }

  private generateExternalPlayerId(): string {
    if (typeof window !== 'undefined') {
      let playerId = localStorage.getItem('tula_external_player_id');
      if (!playerId) {
        playerId = generateId();
        localStorage.setItem('tula_external_player_id', playerId);
      }
      return playerId;
    }
    return generateId();
  }

  private async fetchAndCacheAllVariants(): Promise<void> {
    if (!this.currentPlayer) {
      throw new Error('Player must be set before fetching variants');
    }
    
    const detectedPlatform = getPlatform(this.config.platform);
    const request: GetAllVariantsRequest = {
      userId: this.currentPlayer.externalPlayerId,
      platform: detectedPlatform || 'both'
    };

    debugLog(this.config.debug, 'Fetching all variants for player:', this.currentPlayer.externalPlayerId);

    try {
      const response = await fetch(`${this.config.baseUrl}/api/sdk/get-all-variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-SDK-Version': '0.1.1'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const variantsData: GetAllVariantsResponse = await response.json();
      
      if (!variantsData.success) {
        throw new Error('Failed to fetch variants');
      }

      this.variantCache = variantsData.variants;
      
      const variantCount = Object.keys(this.variantCache).length;
      const variantSummary = Object.entries(this.variantCache).map(([itemId, variant]) => ({
        itemId,
        productId: variant.variant.productId,
        price: `${variant.variant.price.cents}¢ ${variant.variant.price.currency}`,
        quantity: variant.variant.quantity,
        experimentId: variant.experimentId,
        experimentArmId: variant.experimentArmId
      }));
      
      debugLog(this.config.debug, 'Fetched and cached', variantCount, 'variants for instant access');
      debugLog(this.config.debug, 'Cached variants summary:', variantSummary);
    } catch (error) {
      debugLog(this.config.debug, 'Failed to fetch and cache variants:', error);
      // Don't throw here - let the SDK continue to work without cache
      // Individual getItemVariant calls will still work via the API
    }
  }

  getPlayerId(): string | null {
    return this.currentPlayer?.externalPlayerId || null;
  }

  getInternalPlayerId(): string | null {
    return this.currentPlayer?.id || null;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // Core A/B testing functionality - Get item variant with pricing
  // Use TulaItemTracker component to automatically log experiment_view events when items are displayed
  async getItemVariant(
    itemId: string, // Now only accepts developer-friendly itemId
    platform?: 'ios' | 'android' | 'both'
  ): Promise<ItemVariantResponse> {
    this.ensureInitialized();
    
    // First, try to get from cache for instant response
    if (this.variantCache[itemId]) {
      const cachedVariant = this.variantCache[itemId];
      debugLog(this.config.debug, 'Returning cached variant for:', itemId, {
        productId: cachedVariant.variant.productId,
        price: `${cachedVariant.variant.price.cents}¢ ${cachedVariant.variant.price.currency}`,
        quantity: cachedVariant.variant.quantity,
        experimentId: cachedVariant.experimentId,
        experimentArmId: cachedVariant.experimentArmId
      });
      return cachedVariant;
    }

    // If not in cache, fall back to API call
    debugLog(this.config.debug, 'Variant not found in cache, fetching from API for:', itemId);
    
    const detectedPlatform = getPlatform(this.config.platform);
    const finalPlatform = platform || detectedPlatform || 'both';
    
    const request: GetItemVariantRequest = {
      itemId: itemId, // Use developer-friendly itemId
      userId: this.currentPlayer!.externalPlayerId,
      platform: finalPlatform as 'ios' | 'android' | 'both'
    };

    debugLog(this.config.debug, 'Getting item variant:', itemId, 'for platform:', request.platform);

    try {
      const response = await fetch(`${this.config.baseUrl}/api/sdk/get-item-variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-SDK-Version': '0.1.1'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const variantData: ItemVariantResponse = await response.json();
      
      // Cache the newly fetched variant for future use
      this.variantCache[itemId] = variantData;
      
      debugLog(this.config.debug, 'Fetched and cached new variant for:', itemId, {
        productId: variantData.variant.productId,
        price: `${variantData.variant.price.cents}¢ ${variantData.variant.price.currency}`,
        quantity: variantData.variant.quantity,
        experimentId: variantData.experimentId,
        experimentArmId: variantData.experimentArmId,
        isControl: variantData.isControl
      });

      return variantData;
    } catch (error) {
      debugLog(this.config.debug, 'Failed to get item variant:', error);
      throw new Error(`Failed to get item variant: ${error}`);
    }
  }

  // Enhanced event logging with experiment context
  async logEvent(
    eventType: 'store_view' | 'item_click' | 'purchase_start' | 'purchase_complete' | 'purchase_fail' | 'experiment_view',
    properties?: Record<string, any>
  ): Promise<LogEventResponse> {
    this.ensureInitialized();

    const session = this.sessionManager.getCurrentSession();
    
    const request: LogEventRequest = {
      playerId: this.currentPlayer!.id,
      userId: this.currentPlayer!.externalPlayerId,
      sessionId: session?.id,
      eventType,
      itemId: properties?.itemId,
      skuVariantId: properties?.skuVariantId,
      experimentId: properties?.experimentId,
      experimentArmId: properties?.experimentArmId,
      properties: {
        ...properties,
        sdk_version: '0.1.1',
        platform: getPlatform(this.config.platform),
        app_version: getAppVersion(),
        device_id: getDeviceId()
      }
    };

    debugLog(this.config.debug, 'Logging event:', eventType, 'with properties:', properties);

    try {
      const response = await fetch(`${this.config.baseUrl}/api/sdk/log-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-SDK-Version': '0.1.1'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const logResponse: LogEventResponse = await response.json();
      debugLog(this.config.debug, 'Event logged successfully:', logResponse.eventId);
      
      return logResponse;
    } catch (error) {
      debugLog(this.config.debug, 'Failed to log event:', error);
      throw new Error(`Failed to log event: ${error}`);
    }
  }

  // Convenience methods for tracking specific events with experiment context
  async trackPurchaseStart(
    variantData: ItemVariantResponse,
    additionalProperties?: Record<string, any>
  ): Promise<LogEventResponse> {
    return this.logEvent('purchase_start', {
      itemId: variantData.itemId,
      skuVariantId: variantData.variant.skuVariantId,
      experimentId: variantData.experimentId,
      experimentArmId: variantData.experimentArmId,
      price: variantData.variant.price.cents,
      currency: variantData.variant.price.currency,
      quantity: variantData.variant.quantity,
      productId: variantData.variant.productId,
      ...additionalProperties
    });
  }

  async trackPurchaseComplete(
    variantData: ItemVariantResponse,
    transactionId: string,
    additionalProperties?: Record<string, any>
  ): Promise<LogEventResponse> {
    return this.logEvent('purchase_complete', {
      itemId: variantData.itemId,
      skuVariantId: variantData.variant.skuVariantId,
      experimentId: variantData.experimentId,
      experimentArmId: variantData.experimentArmId,
      transactionId,
      priceCents: variantData.variant.price.cents,
      currency: variantData.variant.price.currency,
      quantity: variantData.variant.quantity,
      productId: variantData.variant.productId,
      ...additionalProperties
    });
  }

  async trackPurchaseFail(
    variantData: ItemVariantResponse,
    error: string,
    additionalProperties?: Record<string, any>
  ): Promise<LogEventResponse> {
    return this.logEvent('purchase_fail', {
      itemId: variantData.itemId,
      skuVariantId: variantData.variant.skuVariantId,
      experimentId: variantData.experimentId,
      experimentArmId: variantData.experimentArmId,
      error,
      price: variantData.variant.price.cents,
      currency: variantData.variant.price.currency,
      quantity: variantData.variant.quantity,
      productId: variantData.variant.productId,
      ...additionalProperties
    });
  }

  async trackItemClick(
    variantData: ItemVariantResponse,
    additionalProperties?: Record<string, any>
  ): Promise<LogEventResponse> {
    return this.logEvent('item_click', {
      itemId: variantData.itemId,
      skuVariantId: variantData.variant.skuVariantId,
      experimentId: variantData.experimentId,
      experimentArmId: variantData.experimentArmId,
      price: variantData.variant.price.cents,
      quantity: variantData.variant.quantity,
      ...additionalProperties
    });
  }

  // Auto-tracking utility methods for manual control (DOM environments)
  trackElementAsStore(element: Element, properties?: Record<string, any>): void {
    this.autoTracker.trackElementAsStore(element, properties);
  }

  trackElementAsItem(element: Element, itemVariant: ItemVariantResponse, properties?: Record<string, any>): void {
    this.autoTracker.trackElementAsItem(element, itemVariant, properties);
  }

  enableAutoTracking(): void {
    if (this.initialized) {
      this.autoTracker.start();
    }
  }

  disableAutoTracking(): void {
    this.autoTracker.stop();
  }

  // Manual tracking methods for React Native and other non-DOM environments
  async trackStoreViewManual(properties?: Record<string, any>): Promise<void> {
    return this.autoTracker.trackStoreViewManual(properties);
  }

  async trackItemViewManual(itemId: string, platform: 'ios' | 'android' | 'both' = 'both', properties?: Record<string, any>): Promise<void> {
    return this.autoTracker.trackItemViewManual(itemId, platform, properties);
  }

  // React Native component tracking hooks (fire and forget)
  trackReactNativeStoreView(properties?: Record<string, any>): void {
    this.autoTracker.trackReactNativeStoreView(properties);
  }

  trackReactNativeItemView(itemId: string, platform: 'ios' | 'android' | 'both' = 'both', properties?: Record<string, any>): void {
    this.autoTracker.trackReactNativeItemView(itemId, platform, properties);
  }

  // Utility method to refresh the variant cache
  async refreshVariantCache(): Promise<void> {
    this.ensureInitialized();
    await this.fetchAndCacheAllVariants();
  }

  // Utility method to get cached variant count
  getCachedVariantCount(): number {
    return Object.keys(this.variantCache).length;
  }
}